<template>
  <div class="profile-container">
    <!-- Hero Section -->
    <div class="hero-section text-center py-5">
      <div class="profile-avatar mb-4">
        <img
          src="@/assets/images/peter.jpg"
          alt="Peter Phillip"
          class="profile-image"
        />
      </div>
      <h1 class="display-4 font-weight-bold text-white mb-3">Peter Phillip</h1>
      <p class="lead text-white mb-4">Chief Technology Officer & Technology Entrepreneur</p>
      <p class="text-white max-width-600 mx-auto">
        A technology leader with 18+ years of experience in software engineering, systems architecture, and AI solutions.
        Passionate about building scalable platforms, mentoring future developers, and driving innovation across global markets.
      </p>
    </div>

    <!-- About Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">About Me</h2>
      <div class="row">
        <div class="col-md-8">
          <p class="text-muted">
            I'm a technology entrepreneur and CTO with extensive experience across multiple continents,
            specializing in AI-powered solutions, SaaS platforms, and enterprise-grade systems. My journey
            spans from early software engineering roles to founding and advising multiple ventures.
          </p>
          <p class="text-muted">
            I've led teams in product delivery and innovation, overseen large-scale cloud deployments,
            and introduced AI-driven solutions into core products. Currently focused on building
            high-impact projects including Tiye (Malawian super app), The Circle (luxury estate),
            and Wona (AI-powered anti-theft system).
          </p>
        </div>
        <div class="col-md-4">
          <div class="quick-info">
            <h5 class="text-primary mb-3">Quick Facts</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><strong>Experience:</strong> 18+ Years</li>
              <li class="mb-2"><strong>Focus:</strong> AI, SaaS, Systems Architecture</li>
              <li class="mb-2"><strong>Countries:</strong> 10+ Global Markets</li>
              <li class="mb-2"><strong>Available:</strong> Consulting & Advisory</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Career Timeline Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Career Timeline</h2>
      <div class="timeline">
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content">
            <h5 class="text-primary">Entrepreneurship & High-Impact Projects</h5>
            <!-- <p class="text-muted mb-1">2023 - Present</p> -->
            <p class="text-muted">
              Founded Tiye (Malawian super app), The Circle (luxury estate), BagTag (bag tracking service),
              and Wona (AI-powered anti-theft system). Launching Malawi's secondary school Software Engineering Training Program.
            </p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content">
            <h5 class="text-primary">Expansion into AI, SaaS & Specialized Solutions</h5>
            <!-- <p class="text-muted mb-1">2020 - 2023</p> -->
            <p class="text-muted">
              Founded Kaizen Flow (SaaS process management), developed AI-powered retail theft detection,
              and explored RAG-based AI solutions for business knowledge management.
            </p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content">
            <h5 class="text-primary">Senior Leadership & CTO Roles</h5>
            <!-- <p class="text-muted mb-1">2015 - 2020</p> -->
            <p class="text-muted">
              Served as CTO at Lula Loop, led Jonga security platform, oversaw large-scale
              cloud deployments and introduced AI-driven solutions into core products.
            </p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content">
            <h5 class="text-primary">Systems Architecture & Global Experience</h5>
            <!-- <p class="text-muted mb-1">2010 - 2015</p> -->
            <p class="text-muted">
              Transitioned into systems architecture, worked internationally across multiple continents,
              led integration projects and specialized in MVP development for emerging businesses.
            </p>
          </div>
        </div>
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div class="timeline-content">
            <h5 class="text-primary">Early Software Engineering Career</h5>
            <!-- <p class="text-muted mb-1">2007 - 2010</p> -->
            <p class="text-muted">
              Began professional journey focusing on core software engineering principles, worked across
              corporations and startups, developed skills in frontend/backend development and systems integration.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Skills Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Skills & Expertise</h2>
      <div class="row">
        <div class="col-md-6 mb-4">
          <h5 class="text-primary mb-3">Technical Skills</h5>
          <div class="skill-tags">
            <span class="skill-tag">AI & Computer Vision</span>
            <span class="skill-tag">Systems Architecture</span>
            <span class="skill-tag">Mobile Development</span>
            <span class="skill-tag">Web Applications</span>
            <span class="skill-tag">Microservices</span>
            <span class="skill-tag">Cloud Deployments</span>
            <span class="skill-tag">DevOps</span>
            <span class="skill-tag">Database Design</span>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <h5 class="text-primary mb-3">Technologies & Frameworks</h5>
          <div class="skill-tags">
            <span class="skill-tag">Vue.js</span>
            <span class="skill-tag">Flutter</span>
            <span class="skill-tag">Laravel</span>
            <span class="skill-tag">PostgreSQL</span>
            <span class="skill-tag">Socket.IO</span>
            <span class="skill-tag">MySQL</span>
            <span class="skill-tag">Redis</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Technologies & Specializations Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Technologies & Specializations</h2>
      <div class="row">
        <div class="col-md-8">
          <div class="tech-grid">
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-mobile-alt mr-2"></i>Mobile Applications
              </h6>
              <p class="text-muted mb-0">Hybrid (Flutter, React Native) and Native (Swift for iOS, Android Java/Kotlin)</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-globe mr-2"></i>Web Applications
              </h6>
              <p class="text-muted mb-0">Single Page Applications (SPA) and traditional web platforms</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-plug mr-2"></i>Integrated Systems
              </h6>
              <p class="text-muted mb-0">API-driven solutions, cross-platform communication, and real-time data pipelines</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-cubes mr-2"></i>Microservices Architecture
              </h6>
              <p class="text-muted mb-0">Scalable, modular service-oriented design</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fab fa-microsoft mr-2"></i>Microsoft Tools Extensions
              </h6>
              <p class="text-muted mb-0">Custom add-ins, workflow automations, and system integrations</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-cloud mr-2"></i>Cloud Deployments
              </h6>
              <p class="text-muted mb-0">Multi-tenant SaaS, DevOps pipelines, and containerized environments</p>
            </div>
            <div class="tech-item mb-4">
              <h6 class="text-primary mb-2">
                <i class="fas fa-eye mr-2"></i>AI & Computer Vision
              </h6>
              <p class="text-muted mb-0">Object detection, tracking, and knowledge-based AI models</p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="quick-info">
            <h5 class="text-primary mb-3">Tech Stack</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><strong>Mobile:</strong> Hybrid & Native</li>
              <li class="mb-2"><strong>Web:</strong> SPA & Traditional</li>
              <li class="mb-2"><strong>Architecture:</strong> Microservices</li>
              <li class="mb-2"><strong>Cloud:</strong> Multi-tenant SaaS</li>
              <li class="mb-2"><strong>AI:</strong> Computer Vision</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Projects Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Featured Projects & Ventures</h2>
      <div class="row">
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Tiye</h5>
            <p class="product-tagline">Malawi's everyday super app for services and commerce.</p>
            <p class="text-muted">
              Malawian super app offering marketplace, rides, property, and food delivery services.
              Built with modern mobile technologies and scalable architecture.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Super App</span>
              <span class="product-tag product-tag-green">Malawi</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Wona</h5>
            <p class="product-tagline">AI-powered theft detection and retail intelligence.</p>
            <p class="text-muted">
              AI-powered anti-theft camera system for retail, incorporating stock management tools
              and real-time incident alerts using YOLO and DeepSORT.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">AI Security</span>
              <span class="product-tag product-tag-green">Computer Vision</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">The Circle</h5>
            <p class="product-tagline">Luxury estate and cultural events destination.</p>
            <p class="text-muted">
              Luxury estate and events venue in Malawi, integrating hospitality, arts, and community impact.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Luxury Estate</span>
              <span class="product-tag product-tag-green">Hospitality</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">BagTag</h5>
            <p class="product-tagline">QR-powered peace of mind for every trip.</p>
            <p class="text-muted">
              Annual subscription-based bag tracking service with QR-code-based recovery system.
              Built for travelers and frequent flyers.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">IoT</span>
              <span class="product-tag product-tag-green">Travel</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Tengy</h5>
            <p class="product-tagline">Live ordering that connects crowds to vendors.</p>
            <p class="text-muted">
              Live ordering and discovery platform built for concerts, markets, restaurants, and custom kitchens in office buildings.
              Bridges event experiences with on-site food and vendor services, giving customers a way to see what's happening around them and place real-time orders without hassle.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Live Ordering</span>
              <span class="product-tag product-tag-green">Event Platform</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Finoo</h5>
            <p class="product-tagline">Smart financial companion for students and families.</p>
            <p class="text-muted">
              Student-focused financial service designed to simplify the journey from secondary school to tertiary education, locally or abroad.
              It helps students and their families prepare for tuition payments, manage funds securely, and make direct payments to universities.
              With an integrated wallet, Finoo ensures that students have safe, reliable access to their funds wherever they study.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Education Finance</span>
              <span class="product-tag product-tag-green">Student Platform</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Kaizen Flow</h5>
            <p class="product-tagline">Continuous improvement hub for teams and processes.</p>
            <p class="text-muted">
              Employee support and process management platform that helps organizations streamline onboarding, manage projects, and improve internal workflows.
              It acts as a private, AI-powered assistant that integrates into company systems to provide knowledge management, reporting, employee development recommendations, and cross-project collaboration.
              Designed with adaptability and privacy in mind, Kaizen Flow enables teams to work more efficiently while gaining insights into sentiment, performance, and organizational growth.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Process Management</span>
              <span class="product-tag product-tag-green">AI Assistant</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Pebble</h5>
            <p class="product-tagline">Tools and guidance for healthier relationships.</p>
            <p class="text-muted">
              Relationship support app that helps people prepare for healthy partnerships and sustain them over time.
              It combines readiness tools for singles with maintenance features for couples, offering a structured but fun way to grow together.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Relationship App</span>
              <span class="product-tag product-tag-green">Personal Growth</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="project-card">
            <h5 class="product-name">Jonga</h5>
            <p class="product-tagline">Affordable, connected security for neighborhoods.</p>
            <p class="text-muted">
              Community security platform designed to make neighborhoods safer through affordable, connected alarm systems and community alerts.
              Built to address the high costs of traditional security solutions, Jonga empowers ordinary households to secure their homes
              and stay connected with their neighbors in real time.
            </p>
            <div class="project-tags">
              <span class="product-tag product-tag-green">Community Security</span>
              <span class="product-tag product-tag-green">IoT Platform</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="product-card">
            <h5 class="product-name">Mazi</h5>
            <p class="product-tagline">Smart Water for Malawi and Beyond</p>
            <p class="product-description">
              A locally developed smart water metering and network solution designed to transform how water is managed, distributed, and accessed across Malawi. Built for African realities, Mazi combines intelligent hardware, real-time data, and a resilient communication network to help utilities reduce water losses, improve billing accuracy, and deliver reliable water to communities.
            </p>
            <div class="product-tags">
              <span class="product-tag product-tag-blue">IoT</span>
              <span class="product-tag product-tag-blue">Smart Metering</span>
              <span class="product-tag product-tag-blue">Water Management</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="product-card">
            <h5 class="product-name">Breakers</h5>
            <p class="product-tagline">All-in-One Restaurant Operations Platform</p>
            <p class="product-description">
              Breakers connects guests, floor staff, and the kitchen in real time. Guests scan a QR code to view the menu, place orders, and track progress from "Pending" to "Ready" — no app download required. Built for busy teams, it streamlines service with role-based dashboards for admins, managers, waiters, kitchen, and bar. Restaurants can manage menus and availability, handle tabs and payments, track inventory with low-stock alerts, generate clear sales reports, and plan and print staff schedules so every shift runs smoothly.
            </p>
            <div class="product-tags">
              <span class="product-tag product-tag-orange">Restaurants</span>
              <span class="product-tag product-tag-orange">Operations</span>
              <span class="product-tag product-tag-orange">QR Ordering</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="product-card">
            <h5 class="product-name">Breakers Retail</h5>
            <p class="product-tagline">End-to-End POS for Modern Retail</p>
            <p class="product-description">
              Breakers Retail is a full POS and store operations system built for supermarkets, boutiques, and general retail. It connects tills, inventory, and back office in one platform, with real-time stock, pricing, and sales insights. Available on mobile, web, and native devices, it supports fast checkout, barcode scanning, discounts, staff roles, and branch-level reporting so retailers can manage the entire customer journey from store floor to back office.
            </p>
            <div class="product-tags">
              <span class="product-tag product-tag-green">Retail</span>
              <span class="product-tag product-tag-green">POS</span>
              <span class="product-tag product-tag-green">Multi-Channel</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="product-card">
            <h5 class="product-name">Nara</h5>
            <p class="product-tagline">Modern operating system for salons and spas.</p>
            <p class="product-description">
              Nara is a salon and spa management platform that helps parlours run their entire front desk from one place. Staff can manage bookings and calendars, chat with clients, sell retail products, scan vouchers and loyalty cards, and track sales reports in a clean, mobile-first workflow tailored for African beauty businesses.
            </p>
            <div class="product-tags">
              <span class="product-tag product-tag-blue">Beauty & Wellness</span>
              <span class="product-tag product-tag-blue">Staff Tools</span>
              <span class="product-tag product-tag-blue">Malawi</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <div class="product-card">
            <h5 class="product-name">Chef Nadia</h5>
            <p class="product-tagline">Recipe and menu management for kitchens and caterers.</p>
            <p class="product-description">
              Chef Nadia keeps recipes, ingredients, and menus in one place. Add recipes by pasting a URL or text, or by uploading a PDF or photo; optional AI (Gemini, Groq, Ollama, or OpenAI) cleans and structures them into title, ingredients, and steps. When importing, the app suggests existing ingredients for similar names (e.g. "cake flour" vs "flour") so you can reuse or add new without duplicates. Build menus from your recipes, manage ingredients and suppliers, generate shopping lists, and control costs. Export and import your data for backup or sharing. Designed for chefs, home cooks, and small food businesses who want a single hub for recipes and operations.
            </p>
            <div class="product-tags">
              <span class="product-tag product-tag-orange">Recipes</span>
              <span class="product-tag product-tag-orange">Menus</span>
              <span class="product-tag product-tag-orange">AI Import</span>
              <span class="product-tag product-tag-orange">Ingredients</span>
              <span class="product-tag product-tag-orange">Costing</span>
              <span class="product-tag product-tag-orange">Export</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Experience Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Global Experience</h2>
      <div class="row">
        <div class="col-md-6 mb-4">
          <h5 class="text-primary mb-3">Countries Worked In</h5>
          <div class="skill-tags">
            <span class="skill-tag">Sweden</span>
            <span class="skill-tag">Czech Republic</span>
            <span class="skill-tag">United Kingdom</span>
            <span class="skill-tag">Netherlands</span>
            <span class="skill-tag">South Africa</span>
            <span class="skill-tag">Malawi</span>
            <span class="skill-tag">UAE</span>
            <span class="skill-tag">Brazil</span>
            <span class="skill-tag">Canada</span>
            <span class="skill-tag">United States</span>
          </div>
        </div>
        <div class="col-md-6 mb-4">
          <h5 class="text-primary mb-3">Industries</h5>
          <div class="skill-tags">
            <span class="skill-tag">Banking & Finance</span>
            <span class="skill-tag">Travel & Tourism</span>
            <span class="skill-tag">Security & Surveillance</span>
            <span class="skill-tag">Property & Real Estate</span>
            <span class="skill-tag">Transport & Logistics</span>
            <span class="skill-tag">E-commerce</span>
            <span class="skill-tag">Gaming</span>
            <span class="skill-tag">Government</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Business & Investment Advisory Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Business & Investment Advisory</h2>
      <div class="row">
        <div class="col-md-8">
          <p class="text-muted mb-3">
            Extensive experience advising small businesses on growth strategies, technology adoption, and operational scaling.
          </p>
          <p class="text-muted mb-3">
            Served on large business investment committees, responsible for evaluating and approving investments totaling up to $24 million.
          </p>
          <p class="text-muted mb-3">
            Worked with companies seeking $10 - 30 million funding caps, assessing:
          </p>
          <ul class="text-muted mb-3">
            <li class="mb-2"><strong>Software feasibility</strong> - ensuring products can meet technical and market demands.</li>
            <li class="mb-2"><strong>Operational strength</strong> - evaluating leadership, execution capacity, and scalability.</li>
            <li class="mb-2"><strong>Track record & achievements</strong> - measuring proven success and market positioning.</li>
          </ul>
          <p class="text-muted">
            Advised on investment risk management, ROI potential, and market fit for large-scale projects.
          </p>
        </div>
        <div class="col-md-4">
          <div class="quick-info">
            <h5 class="text-primary mb-3">Investment Experience</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><strong>Committee Role:</strong> Investment Evaluation</li>
              <li class="mb-2"><strong>Portfolio Size:</strong> Up to $24M</li>
              <li class="mb-2"><strong>Funding Range:</strong> $10M - $30M</li>
              <li class="mb-2"><strong>Focus:</strong> Tech & Business</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Adhoc Projects & Contract Work Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Adhoc Projects & Contract Work</h2>
      <div class="row">
        <div class="col-md-8">
          <p class="text-muted mb-3">
            Providing on-demand technology expertise for both corporates and founders. I offer comprehensive services that help businesses scale, modernize, and optimize their operations.
          </p>
          <p class="text-muted mb-3">
            My approach combines strategic thinking with hands-on implementation, ensuring that every project delivers measurable impact and long-term value.
          </p>
        </div>
        <div class="col-md-4">
          <div class="quick-info">
            <h5 class="text-primary mb-3">Service Highlights</h5>
            <ul class="list-unstyled">
              <li class="mb-2"><strong>Platform Design</strong> - Robust, scalable architecture</li>
              <li class="mb-2"><strong>MVP Development</strong> - Rapid validation & market entry</li>
              <li class="mb-2"><strong>System Troubleshooting</strong> - Critical issue resolution</li>
              <li class="mb-2"><strong>Digital Growth Strategy</strong> - Technology transformation</li>
            </ul>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-md-6 mb-3">
          <div class="service-item">
            <h6 class="text-primary"><i class="fas fa-archway mr-2"></i>Platform Design</h6>
            <p class="text-muted mb-0">Architecting and structuring robust, scalable digital platforms that grow with your business.</p>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="service-item">
            <h6 class="text-primary"><i class="fas fa-rocket mr-2"></i>MVP Development</h6>
            <p class="text-muted mb-0">Creating minimum viable products for rapid validation and market entry with proven methodologies.</p>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="service-item">
            <h6 class="text-primary"><i class="fas fa-tools mr-2"></i>System Troubleshooting</h6>
            <p class="text-muted mb-0">Diagnosing and fixing critical issues in existing platforms to restore optimal performance.</p>
          </div>
        </div>
        <div class="col-md-6 mb-3">
          <div class="service-item">
            <h6 class="text-primary"><i class="fas fa-chart-line mr-2"></i>Digital Growth Strategy</h6>
            <p class="text-muted mb-0">Recommending technology-driven transformations for business scalability and modernization.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Companies & Partners Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Employment, Partners & Integrations</h2>
      <div class="skill-tags">
        <span class="skill-tag">24.com</span>
        <span class="skill-tag">5rooms</span>
        <span class="skill-tag">ABSA</span>
        <span class="skill-tag">Adidas</span>
        <span class="skill-tag">Africa Internet Accelerator</span>
        <span class="skill-tag">Avenga</span>
        <span class="skill-tag">Barloworld</span>
        <span class="skill-tag">Bluegrass Digital</span>
        <span class="skill-tag">Citizencode</span>
        <span class="skill-tag">Converse</span>
        <span class="skill-tag">FNB</span>
        <span class="skill-tag">Fomo Travel</span>
        <span class="skill-tag">Girleffect</span>
        <span class="skill-tag">Hellopeter</span>
        <span class="skill-tag">Jonga</span>
        <span class="skill-tag">KKCG</span>
        <span class="skill-tag">Kinderelo</span>
        <span class="skill-tag">Kujali</span>
        <span class="skill-tag">Lego</span>
        <span class="skill-tag">Lighthouse</span>
        <span class="skill-tag">Lightswitch</span>
        <span class="skill-tag">Locumbase</span>
        <span class="skill-tag">Lulaloop</span>
        <span class="skill-tag">Media24</span>
        <span class="skill-tag">Medimark</span>
        <span class="skill-tag">Mindtrix Media</span>
        <span class="skill-tag">Nameyourhood</span>
        <span class="skill-tag">Ndalo</span>
        <span class="skill-tag">Neerist</span>
        <span class="skill-tag">Nucleus</span>
        <span class="skill-tag">Savebox - Old Mutual</span>
        <span class="skill-tag">Seabottle</span>
        <span class="skill-tag">Sealedbid</span>
        <span class="skill-tag">Seeff</span>
        <span class="skill-tag">Takealot</span>
        <span class="skill-tag">Tengy</span>
        <span class="skill-tag">Thinkmoney</span>
        <span class="skill-tag">Vigilant</span>
        <span class="skill-tag">Webafrica</span>
        <span class="skill-tag">style36</span>
      </div>
    </div>

    <!-- Contact Section -->
    <div class="section-card mb-5">
      <h2 class="section-title">Get In Touch</h2>
      <div class="text-center">
        <p class="text-muted mb-4">
          I'm always interested in hearing about new opportunities, exciting projects, and technology partnerships.
          Feel free to reach out if you'd like to connect or discuss potential collaborations!
        </p>
        <div class="contact-info mb-4">
          <div class="row">
            <div class="col-md-4 mb-3">
              <h6 class="text-primary">Email</h6>
              <p class="text-muted">peterphillip99@gmail.com</p>
              <p class="text-muted">peter@tiye.services</p>
            </div>
            <div class="col-md-4 mb-3">
              <h6 class="text-primary">Phone</h6>
              <p class="text-muted">Malawi: +265 89 320 1114</p>
              <p class="text-muted">South Africa: +27 72 900 6987</p>
              <p class="text-muted">Netherlands: +31 68 47 555 21</p>
            </div>
            <div class="col-md-4 mb-3">
              <h6 class="text-primary">Calendar</h6>
              <p class="text-muted">Book a meeting</p>
              <a href="https://calendly.com/peterphillip" target="_blank" class="btn btn-sm btn-outline-earth">Calendly</a>
            </div>
          </div>
        </div>
        <div class="contact-buttons">
          <a href="mailto:peterphillip99@gmail.com" class="btn btn-earth btn-lg mr-3">
            <i class="fas fa-envelope mr-2"></i>Email Me
          </a>
          <a href="https://calendly.com/peterphillip" target="_blank" class="btn btn-outline-earth btn-lg">
            <i class="fas fa-calendar mr-2"></i>Book Meeting
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Home",
  data() {
    return {
      // Add your data here
    };
  },
  mounted() {
    this.initScrollAnimations();
  },
  methods: {
    // Add your methods here
    initScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, observerOptions);

      // Observe all elements with animate-on-scroll class
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach(el => observer.observe(el));
    }
  },
};
</script>

<style scoped>
.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

  .hero-section {
    background: linear-gradient(-45deg, #8B4513, #A0522D, #CD853F, #D2691E);
    background-size: 400% 400%;
    animation: gradientShift 8s ease infinite;
    border-radius: 20px;
    margin-bottom: 3rem;
    position: relative;
    overflow: hidden;
    transform-style: preserve-3d;
  }

  .hero-section:hover {
    animation-play-state: paused;
    transform: scale(1.02);
    transition: transform 0.3s ease;
  }

  .hero-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
    animation: shimmer 8s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes shimmer {
    0%, 100% {
      opacity: 0.1;
      transform: translateX(-100%);
    }
    50% {
      opacity: 0.3;
      transform: translateX(100%);
    }
  }

.hero-section h1,
.hero-section p {
  color: white;
}

.max-width-600 {
  max-width: 600px;
}

  .section-card {
    background: #FAF0E6;
    border-radius: 15px;
    padding: 2rem;
    box-shadow: 0 4px 6px rgba(139, 69, 19, 0.1);
    border: 1px solid #D2B48C;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform: translateZ(0);
  }

  .section-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 25px rgba(139, 69, 19, 0.2);
    border-color: #8B4513;
  }

  .section-title {
    color: #8B4513;
    margin-bottom: 1.5rem;
    font-weight: 600;
    position: relative;
    padding-bottom: 0.5rem;
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: #8B4513;
    border-radius: 2px;
    animation: pulseWidth 2s ease-in-out infinite;
  }

  @keyframes pulseWidth {
    0%, 100% {
      width: 50px;
    }
    50% {
      width: 80px;
    }
  }

.profile-avatar {
  display: flex;
  justify-content: center;
}

            .profile-image {
              width: 120px;
              height: 120px;
              border-radius: 50%;
              object-fit: cover;
              border: 4px solid white;
              box-shadow: 0 0 0 4px #e2e8f0;
              animation: float 6s ease-in-out infinite;
              transition: all 0.3s ease;
            }

            .profile-image:hover {
              transform: scale(1.1) rotate(5deg);
              box-shadow: 0 0 20px rgba(139, 69, 19, 0.6);
            }

            @keyframes float {
              0%, 100% {
                transform: translateY(0px);
              }
              50% {
                transform: translateY(-10px);
              }
            }

  .quick-info {
    background: #F5F5DC;
    padding: 1.5rem;
    border-radius: 10px;
    border-left: 4px solid #8B4513;
  }

.skill-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.skill-tag {
  background: #8B4513;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  cursor: pointer;
  display: inline-block;
}

.skill-tag:hover {
  transform: translateY(-3px) scale(1.1);
  background: #A0522D;
  box-shadow: 0 8px 15px rgba(139, 69, 19, 0.4);
}

.timeline {
  position: relative;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e2e8f0;
}

.timeline-item {
  position: relative;
  padding-left: 60px;
  margin-bottom: 2rem;
}

  .timeline-marker {
    position: absolute;
    left: 11px;
    top: 0;
    width: 20px;
    height: 20px;
    background: #8B4513;
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: 0 0 0 4px #D2B48C;
  }

.timeline-content h5 {
  margin-bottom: 0.5rem;
}

  .project-card {
    background: #F5F5DC;
    padding: 2rem;
    border-radius: 10px;
    height: 100%;
    border: 1px solid #D2B48C;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    cursor: pointer;
  }

  .project-card .text-muted {
    margin-bottom: 1.25rem;
  }

  .project-card .project-tags {
    margin-top: 1.25rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .project-card:hover {
    transform: translateY(-5px) scale(1.03);
    background: #FAF0E6;
    border-color: #8B4513;
    box-shadow: 0 10px 20px rgba(139, 69, 19, 0.15);
  }

  .product-card {
    background: #F5F5DC;
    padding: 2rem;
    border-radius: 15px;
    height: 100%;
    border: 1px solid #D2B48C;
    box-shadow: 0 4px 6px rgba(139, 69, 19, 0.1);
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    display: flex;
    flex-direction: column;
    cursor: pointer;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    box-sizing: border-box;
  }

  .product-card:hover {
    transform: translateY(-5px) scale(1.02);
    background: #FAF0E6;
    border-color: #8B4513;
    box-shadow: 0 10px 20px rgba(139, 69, 19, 0.15);
  }

  .product-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 1.5rem;
    color: white;
    box-shadow: 0 4px 8px rgba(139, 69, 19, 0.2);
  }

  .product-icon-blue {
    background-color: #8B4513;
  }

  .product-icon-orange {
    background-color: #CD853F;
  }

  .product-icon-green {
    background-color: #A0522D;
  }

  .product-name {
    font-weight: bold;
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
    color: #8B4513;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .product-tagline {
    color: #8B7355;
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
    font-style: italic;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .product-description {
    color: #8B7355;
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1.25rem;
    flex-grow: 1;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .product-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0;
  }

  .product-tag {
    padding: 0.4rem 0.9rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    display: inline-block;
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  }

  .product-tag:hover {
    transform: translateY(-2px) scale(1.05);
  }

  .product-tag-blue {
    background-color: #F5F5DC;
    color: #8B4513;
    border: 1px solid #D2B48C;
  }

  .product-tag-orange {
    background-color: #FAF0E6;
    color: #A0522D;
    border: 1px solid #D2B48C;
  }

  .product-tag-green {
    background-color: #F5F5DC;
    color: #8B4513;
    border: 1px solid #D2B48C;
  }

.project-image-placeholder {
  width: 100%;
  height: 120px;
  background: #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #64748b;
}

.project-card .project-tags .product-tag {
  margin: 0;
}

  .contact-info {
    background: #F5F5DC;
    padding: 1.5rem;
    border-radius: 10px;
    border: 1px solid #D2B48C;
  }

.contact-buttons .btn {
  margin-bottom: 0.5rem;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

  .tech-item {
    background: #F5F5DC;
    padding: 1rem;
    border-radius: 10px;
    border: 1px solid #D2B48C;
    text-align: center;
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    cursor: pointer;
  }

  .tech-item:hover {
    transform: translateY(-3px) scale(1.05);
    background: #FAF0E6;
    border-color: #8B4513;
    box-shadow: 0 8px 15px rgba(139, 69, 19, 0.2);
  }

.tech-item h6 {
  margin-bottom: 0.5rem;
}

  .tech-item p {
    font-size: 0.9rem;
    color: #8B7355;
  }

  /* Enhanced Scroll Animation Styles */
  .animate-on-scroll {
    opacity: 0;
    transform: translateY(80px) scale(0.95);
    transition: all 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    filter: blur(2px);
  }

  .animate-on-scroll.animate-in {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }

              /* Enhanced staggered animations for child elements */
            .animate-on-scroll.animate-in .skill-tag,
            .animate-on-scroll.animate-in .tech-item,
            .animate-on-scroll.animate-in .timeline-item,
            .animate-on-scroll.animate-in .project-card,
            .animate-on-scroll.animate-in .service-item {
              animation: flyInEnhanced 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
            }

            /* Add typing effect to hero text */
            .hero-section h1 {
              animation: typewriter 2s steps(40, end) 0.5s both;
              overflow: hidden;
              white-space: nowrap;
            }

            .hero-section .lead {
              animation: fadeInUp 1.5s ease 1.5s both;
            }

            .hero-section p {
              animation: fadeInUp 1.5s ease 2s both;
            }

            @keyframes typewriter {
              from {
                width: 0;
              }
              to {
                width: 100%;
              }
            }

  .animate-on-scroll.animate-in .skill-tag:nth-child(1) { animation-delay: 0.1s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(2) { animation-delay: 0.2s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(3) { animation-delay: 0.3s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(4) { animation-delay: 0.4s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(5) { animation-delay: 0.5s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(6) { animation-delay: 0.6s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(7) { animation-delay: 0.7s; }
  .animate-on-scroll.animate-in .skill-tag:nth-child(8) { animation-delay: 0.8s; }

  .animate-on-scroll.animate-in .tech-item:nth-child(1) { animation-delay: 0.1s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(2) { animation-delay: 0.2s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(3) { animation-delay: 0.3s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(4) { animation-delay: 0.4s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(5) { animation-delay: 0.5s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(6) { animation-delay: 0.6s; }
  .animate-on-scroll.animate-in .tech-item:nth-child(7) { animation-delay: 0.7s; }

  .animate-on-scroll.animate-in .timeline-item:nth-child(1) { animation-delay: 0.1s; }
  .animate-on-scroll.animate-in .timeline-item:nth-child(2) { animation-delay: 0.2s; }
  .animate-on-scroll.animate-in .timeline-item:nth-child(3) { animation-delay: 0.3s; }
  .animate-on-scroll.animate-in .timeline-item:nth-child(4) { animation-delay: 0.4s; }
  .animate-on-scroll.animate-in .timeline-item:nth-child(5) { animation-delay: 0.5s; }

              .animate-on-scroll.animate-in .project-card:nth-child(1) { animation-delay: 0.1s; }
            .animate-on-scroll.animate-in .project-card:nth-child(2) { animation-delay: 0.2s; }
            .animate-on-scroll.animate-in .project-card:nth-child(3) { animation-delay: 0.3s; }
            .animate-on-scroll.animate-in .project-card:nth-child(4) { animation-delay: 0.4s; }
            .animate-on-scroll.animate-in .project-card:nth-child(5) { animation-delay: 0.5s; }
            .animate-on-scroll.animate-in .project-card:nth-child(6) { animation-delay: 0.6s; }
            .animate-on-scroll.animate-in .project-card:nth-child(7) { animation-delay: 0.7s; }
            .animate-on-scroll.animate-in .project-card:nth-child(8) { animation-delay: 0.8s; }
            .animate-on-scroll.animate-in .project-card:nth-child(9) { animation-delay: 0.9s; }

  @keyframes flyInEnhanced {
    0% {
      opacity: 0;
      transform: translateY(60px) scale(0.8) rotateX(15deg);
    }
    50% {
      opacity: 0.8;
      transform: translateY(-8px) scale(1.05) rotateX(-5deg);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1) rotateX(0deg);
    }
  }

  /* New vibrant animations */
  @keyframes slideInLeft {
    0% {
      opacity: 0;
      transform: translateX(-100px) skewX(-15deg);
    }
    100% {
      opacity: 1;
      transform: translateX(0) skewX(0deg);
    }
  }

  @keyframes slideInRight {
    0% {
      opacity: 0;
      transform: translateX(100px) skewX(15deg);
    }
    100% {
      opacity: 1;
      transform: translateX(0) skewX(0deg);
    }
  }

  @keyframes bounceIn {
    0% {
      opacity: 0;
      transform: scale(0.3) rotate(180deg);
    }
    50% {
      opacity: 1;
      transform: scale(1.1) rotate(90deg);
    }
    100% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }
  }

  @keyframes fadeInScale {
    0% {
      opacity: 0;
      transform: scale(0.5) rotateY(90deg);
    }
    100% {
      opacity: 1;
      transform: scale(1) rotateY(0deg);
    }
  }

  /* Override Bootstrap text-muted for earth tone theme */
  .text-muted {
    color: #8B7355 !important;
  }

  /* Override Bootstrap text-primary for earth tone theme */
  .text-primary {
    color: #8B4513 !important;
  }

              /* Custom earth tone badge and button styles */
            .badge-earth-primary {
              background-color: #8B4513 !important;
              color: white !important;
            }

            .badge-earth-secondary {
              background-color: #A0522D !important;
              color: white !important;
            }

            .btn-earth {
              background-color: #8B4513 !important;
              border-color: #8B4513 !important;
              color: white !important;
            }

            .btn-earth:hover {
              background-color: #A0522D !important;
              border-color: #A0522D !important;
              color: white !important;
            }

            .btn-outline-earth {
              color: #8B4513 !important;
              border-color: #8B4513 !important;
              background-color: transparent !important;
            }

            .btn-outline-earth:hover {
              color: white !important;
              background-color: #8B4513 !important;
              border-color: #8B4513 !important;
            }

  @media (max-width: 768px) {
  .profile-container {
    padding: 1rem;
  }

  .section-card {
    padding: 1.5rem;
  }

  .hero-section {
    padding: 2rem 1rem;
  }

  .contact-buttons .btn {
    display: block;
    width: 100%;
    margin-bottom: 1rem;
  }

  .product-card {
    padding: 1.5rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .product-name {
    font-size: 1.25rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .product-tagline {
    font-size: 0.875rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .product-description {
    font-size: 0.85rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .product-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .product-tag {
    font-size: 0.75rem;
    padding: 0.35rem 0.75rem;
  }
}
</style>
